import type { FC } from 'react';
import React, { useEffect } from 'react';

import { getScroll, usePrevious } from './helpers';
import type { TAutocomplete } from './models';

export const Autocomplete: FC<React.PropsWithChildren<TAutocomplete>> = ({
    autocompleteRef: ref,
    children,
    highlightedItem = -1,
    onEmptyListMessageClick,
    onSelect,
    options,
    allowToShowEmptyListMessage = false,
    styles,
    useNamedContainers,
}) => {
    const prevIndex = usePrevious(highlightedItem);

    const { emptyListMessage } = useNamedContainers(children);
    const showEmptyListMessage = allowToShowEmptyListMessage && !options.length && emptyListMessage;

    useEffect(() => {
        if (highlightedItem < 0 || !ref?.current) return;

        // eslint-disable-next-line no-param-reassign
        ref.current.scrollTop = getScroll({
            highlightedItem,
            prevIndex,
            scrollTop: ref.current.scrollTop,
        });
    }, [highlightedItem, ref, prevIndex]);

    return (
        <ul
            ref={ref}
            className={styles.autocomplete}
            data-test-id="autocomplete"
            role="listbox"
            tabIndex={-1}
        >
            {showEmptyListMessage && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                <li data-test-id="autocomplete-0" onClick={() => onEmptyListMessageClick?.()}>
                    {emptyListMessage}
                </li>
            )}
            {options.map((option, index) => (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <li
                    key={option.id + option.label}
                    aria-selected={highlightedItem === index}
                    className={styles.option}
                    data-test-id={`autocomplete-${index}`}
                    onClick={event => onSelect?.({ event, value: option })}
                    role="option"
                >
                    {option.label}
                    {option.subLabel && <div className={styles.subLabel}>{option.subLabel}</div>}
                </li>
            ))}
        </ul>
    );
};
